import React, { useEffect, useRef, useState } from 'react';
import { RotateCcw, Monitor, Smartphone, Lock } from 'lucide-react';
import { API, BRAND_ID } from './api';
import { btnGhost, inputCls } from './ui';

// api.glowytics.com iframe'e kapalı (nginx); widget script ile bir container'a gömülür.
// Arka planda müşterinin ana sayfasının ekran görüntüsü durur (public/chat-preview-*.jpg — alt klasör setupProxy'de 502 verir —, pixel istekleri kesilerek
// çekildi; iframe'de script'siz site boş görünüyordu). Widget kutusu ayardaki konum + boşluklarla köşeye yerleşir.
// Önizleme modeli: ilk yüklemede previewModel, çalışırken __glowyChat.setPreviewModel(id); boş = ayardaki model.
export default function LivePreview({ config, models = [], settingsModel = '' }) {
  const [model, setModel] = useState('');
  const [device, setDevice] = useState('desktop');
  const modelRef = useRef('');
  const boxRef = useRef(null);
  const latest = useRef(config);
  latest.current = config;
  // Güvenlik sekmesinde seçilen (henüz kaydedilmemiş olabilir) model; açılır listede seçim yoksa o kullanılır.
  const latestModel = useRef(settingsModel);
  latestModel.current = settingsModel;

  useEffect(() => {
    const box = boxRef.current;
    window.__glowyticsChat = { brandId: BRAND_ID, apiBase: API, preview: true, previewConfig: latest.current, container: box, previewModel: modelRef.current || latestModel.current || undefined };
    const s = document.createElement('script');
    s.src = `${API}/pixel/glowytics-chat.js?v=${Date.now()}`;
    s.async = true;
    document.body.appendChild(s);
    return () => {
      s.remove();
      delete window.__glowyticsChat;
      delete window.__glowyChat;
      if (box) box.innerHTML = '';
    };
  }, []);

  const changeModel = (v) => {
    setModel(v);
    modelRef.current = v;
    if (window.__glowyChat && window.__glowyChat.setPreviewModel) window.__glowyChat.setPreviewModel(v || settingsModel);
  };

  useEffect(() => {
    if (!modelRef.current && window.__glowyChat && window.__glowyChat.setPreviewModel) window.__glowyChat.setPreviewModel(settingsModel);
  }, [settingsModel]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (window.__glowyChat) window.__glowyChat.applyConfig(config);
    }, 300);
    return () => clearTimeout(t);
  }, [config]);

  const a = (config && config.appearance) || {};
  const left = a.position === 'left';
  const ox = Math.min(Math.max(Number(a.offsetX) || 0, 0), 80);
  const oy = Math.min(Math.max(Number(a.offsetY) || 0, 0), 80);
  const mobile = device === 'mobile';
  const fullscreen = mobile && a.mobileFullscreen !== false;
  // Widget kutusu: masaüstünde sitenin köşesinde 380px panel; mobilde tam ekran ya da alttan sayfa.
  const boxStyle = mobile
    ? (fullscreen ? { inset: 0 } : { left: 12, right: 12, bottom: 12, height: '78%' })
    : { [left ? 'left' : 'right']: ox, bottom: oy, width: 380, height: `min(600px, calc(100% - ${oy + 24}px))` };

  return (
    <div className="flex flex-col flex-1 min-w-0 min-h-[560px]">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="mr-auto text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Canlı önizleme</h3>
        <div className="flex rounded-lg border border-gray-200 p-0.5 bg-white" role="group" aria-label="Cihaz">
          <button type="button" onClick={() => setDevice('desktop')} title="Masaüstü" className={`p-1.5 rounded-md ${!mobile ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-700'}`}><Monitor className="w-4 h-4" /></button>
          <button type="button" onClick={() => setDevice('mobile')} title="Mobil" className={`p-1.5 rounded-md ${mobile ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-700'}`}><Smartphone className="w-4 h-4" /></button>
        </div>
        <select className={`${inputCls} !w-auto max-w-[260px] !py-1 !text-xs`} value={model} onChange={(e) => changeModel(e.target.value)} aria-label="Önizleme modeli">
          <option value="">Model: ayardaki{settingsModel ? ` (${(models.find((m) => m.id === settingsModel) || {}).label || settingsModel})` : ''}</option>
          {models.map((m) => <option key={m.id} value={m.id}>{m.label || m.id}</option>)}
        </select>
        <button className={btnGhost} onClick={() => window.__glowyChat && window.__glowyChat.reset()}><RotateCcw />Sıfırla</button>
      </div>

      <div className={`flex-1 min-h-0 flex ${mobile ? 'justify-center items-start' : ''}`}>
        <div
          className={mobile
            ? 'relative w-[390px] h-[780px] max-h-full rounded-[44px] border-[10px] border-gray-900 bg-gray-900 shadow-xl overflow-hidden flex flex-col'
            : 'relative w-full h-full rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col'}
        >
          {!mobile && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
              <span className="flex gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-red-300" /><i className="w-2.5 h-2.5 rounded-full bg-amber-300" /><i className="w-2.5 h-2.5 rounded-full bg-green-300" /></span>
              <div className="flex-1 flex items-center gap-1.5 rounded-md bg-white border border-gray-200 px-2.5 py-1 text-xs text-gray-500 truncate">
                <Lock className="w-3 h-3" />hosgelsin.com
              </div>
            </div>
          )}
          <div className={`relative flex-1 min-h-0 bg-white ${mobile ? 'rounded-[34px] overflow-hidden' : ''}`}>
            <img
              src={`/chat-preview-hosgelsin-${mobile ? 'mobile' : 'desktop'}.jpg`}
              alt=""
              className={mobile ? "absolute inset-0 w-full h-full object-cover object-top select-none pointer-events-none" : "absolute top-0 left-0 w-full h-auto select-none pointer-events-none"}
              draggable={false}
            />
            <div
              ref={boxRef}
              className={`absolute ${fullscreen ? '' : 'rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.24)]'} overflow-hidden`}
              style={boxStyle}
            />
          </div>
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        Arka plandaki site yalnızca ekran görüntüsüdür. Kaydetmediğin değişiklikler de önizlemede hemen geçerli olur; önizleme mesajları analitiğe sayılmaz.
      </p>
    </div>
  );
}
